/**
 * Solar Color System
 * Dynamic color mapping based on actual sun altitude and position
 * Automatically adjusts for seasons and geographical location
 */

import SunCalc from 'suncalc';

/**
 * Altitude-based color mapping
 * Maps sun elevation angle to colors throughout the day
 * Optimized for seasonal variations where max altitude varies
 */
const ALTITUDE_COLOR_MAP = [
  { altitude: -90, color: '#0a0a0a', name: 'Deep Night' },        // Lowest possible
  { altitude: -18, color: '#1e1b4b', name: 'Astronomical Night' }, // Complete darkness
  { altitude: -12, color: '#312e81', name: 'Nautical Twilight' },  // Stars visible
  { altitude: -6, color: '#4c1d95', name: 'Civil Twilight' },      // Artificial light needed
  { altitude: -3, color: '#6d28d9', name: 'Blue Hour' },           // Deep blue sky
  { altitude: 0, color: '#9333ea', name: 'Horizon' },              // Sun at horizon
  { altitude: 2, color: '#dc2626', name: 'Sunrise/Sunset Red' },   // Deep red
  { altitude: 4, color: '#ea580c', name: 'Dawn/Dusk Orange' },     // Deep orange
  { altitude: 6, color: '#f97316', name: 'Golden Hour' },          // Orange
  { altitude: 8, color: '#fb923c', name: 'Late Golden Hour' },     // Light orange
  { altitude: 10, color: '#fdba74', name: 'Post-Sunrise' },        // Peach
  { altitude: 12, color: '#fed7aa', name: 'Early Morning Glow' },  // Light peach
  { altitude: 14, color: '#fef3c7', name: 'Morning Light' },       // Cream
  { altitude: 16, color: '#fde047', name: 'Morning Yellow' },      // Bright yellow
  { altitude: 18, color: '#facc15', name: 'Mid-Morning Gold' },    // Gold
  { altitude: 20, color: '#eab308', name: 'Late Morning Gold' },   // Rich gold
  { altitude: 22, color: '#ca8a04', name: 'Pre-Noon Amber' },      // Dark amber
  { altitude: 24, color: '#a16207', name: 'Approaching Noon' },    // Brown-gold
  { altitude: 26, color: '#92400e', name: 'Near Noon Bronze' },    // Bronze
  { altitude: 28, color: '#b45309', name: 'High Sun Amber' },      // Amber
  { altitude: 30, color: '#d97706', name: 'Noon Approach Gold' },  // Orange-gold
  { altitude: 32, color: '#f59e0b', name: 'High Noon Amber' },     // Bright amber
  { altitude: 35, color: '#fbbf24', name: 'Solar Noon Yellow' },   // Yellow
  { altitude: 40, color: '#fde68a', name: 'Peak Sun Light' },      // Light yellow
  { altitude: 45, color: '#fef3c7', name: 'Maximum Light' },       // Pale yellow
  { altitude: 50, color: '#fffbeb', name: 'Solar Peak White' },    // Near white
  { altitude: 60, color: '#fefce8', name: 'Solar Maximum' },       // Warm white
  { altitude: 70, color: '#fef9c3', name: 'Zenith Glow' },         // Pale gold
  { altitude: 80, color: '#fef08a', name: 'Solar Zenith' },        // Light gold
  { altitude: 90, color: '#fde047', name: 'Absolute Zenith' }      // Bright yellow
];

/**
 * Get dynamic solar-based color for the current time
 * @param {Date} date - Current date/time
 * @param {number} latitude - Location latitude
 * @param {number} longitude - Location longitude
 * @param {Object} options - Optional configuration including prayer times
 * @returns {Object} Color information with hex value and metadata
 */
export function getDynamicSolarColor(date, latitude, longitude, options = {}) {
  // Get sun position (altitude and azimuth)
  const sunPos = SunCalc.getPosition(date, latitude, longitude);
  const sunAltitudeDeg = sunPos.altitude * 180 / Math.PI;
  const sunAzimuthDeg = sunPos.azimuth * 180 / Math.PI;
  
  // Get sun times for additional context
  const sunTimes = SunCalc.getTimes(date, latitude, longitude);
  
  // Get base color from sun altitude
  const baseColorInfo = getColorByAltitude(sunAltitudeDeg);
  
  // Apply azimuth-based adjustments for morning vs evening
  const adjustedColor = applyAzimuthAdjustment(
    baseColorInfo.color, 
    sunAltitudeDeg, 
    sunAzimuthDeg,
    sunTimes,
    date
  );
  
  // Handle special night period colors if prayer times are provided
  if (sunAltitudeDeg < -6 && options.fajrTime) {
    const nightColor = getNightPeriodColor(
      date,
      sunTimes,
      options.fajrTime,
      options.maghribTime,
      sunAltitudeDeg
    );
    
    return {
      color: nightColor.color,
      name: nightColor.name,
      phase: nightColor.phase,
      altitude: sunAltitudeDeg,
      azimuth: sunAzimuthDeg
    };
  }
  
  return {
    color: adjustedColor,
    name: baseColorInfo.name,
    phase: determinePhase(sunAltitudeDeg, sunAzimuthDeg),
    altitude: sunAltitudeDeg,
    azimuth: sunAzimuthDeg,
    interpolation: baseColorInfo.interpolation
  };
}

/**
 * Get color based on sun altitude using interpolation
 * @param {number} altitude - Sun altitude in degrees
 * @returns {Object} Color and metadata
 */
function getColorByAltitude(altitude) {
  // Find the two altitude stops that bracket the current altitude
  for (let i = 0; i < ALTITUDE_COLOR_MAP.length - 1; i++) {
    const lower = ALTITUDE_COLOR_MAP[i];
    const upper = ALTITUDE_COLOR_MAP[i + 1];
    
    if (altitude >= lower.altitude && altitude <= upper.altitude) {
      // Calculate interpolation factor
      const range = upper.altitude - lower.altitude;
      const progress = (altitude - lower.altitude) / range;
      
      // Interpolate between colors
      const color = interpolateHexColors(lower.color, upper.color, progress);
      
      return {
        color,
        name: progress < 0.5 ? lower.name : upper.name,
        interpolation: {
          from: lower,
          to: upper,
          progress
        }
      };
    }
  }
  
  // Handle edge cases
  if (altitude < ALTITUDE_COLOR_MAP[0].altitude) {
    return {
      color: ALTITUDE_COLOR_MAP[0].color,
      name: ALTITUDE_COLOR_MAP[0].name,
      interpolation: null
    };
  }
  
  const last = ALTITUDE_COLOR_MAP[ALTITUDE_COLOR_MAP.length - 1];
  return {
    color: last.color,
    name: last.name,
    interpolation: null
  };
}

/**
 * Apply azimuth-based color adjustment for morning vs evening
 * @param {string} baseColor - Base color from altitude
 * @param {number} altitude - Sun altitude in degrees
 * @param {number} azimuth - Sun azimuth in degrees
 * @param {Object} sunTimes - Sun times for the day
 * @param {Date} currentTime - Current time
 * @returns {string} Adjusted color
 */
function applyAzimuthAdjustment(baseColor, altitude, azimuth, sunTimes, currentTime) {
  // Only apply adjustment during low sun angles
  if (altitude < 0 || altitude > 30) {
    return baseColor;
  }
  
  // Determine if morning or evening based on solar noon
  const isMorning = currentTime < sunTimes.solarNoon;
  
  // Convert color to HSL for adjustment
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;
  
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  // Adjust hue based on time of day
  if (altitude >= 0 && altitude <= 15) {
    if (isMorning) {
      // Morning: shift toward cooler colors (blue/cyan)
      // Reduce red component, enhance blue
      hsl.h = (hsl.h - 0.05 + 1) % 1; // Shift hue toward blue
      hsl.s = Math.min(1, hsl.s * 1.1); // Slightly more saturated
    } else {
      // Evening: shift toward warmer colors (orange/red)
      // Enhance red component, reduce blue
      hsl.h = (hsl.h + 0.03) % 1; // Shift hue toward red
      hsl.s = Math.min(1, hsl.s * 1.15); // More saturated for sunset
      hsl.l = Math.max(0, hsl.l - 0.05); // Slightly darker for sunset
    }
  }
  
  // Convert back to hex
  const adjustedRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
}

/**
 * Get special colors for night periods based on time
 * @param {Date} currentTime - Current time
 * @param {Object} sunTimes - Sun times
 * @param {Date} fajrTime - Fajr prayer time
 * @param {Date} maghribTime - Maghrib prayer time  
 * @param {number} altitude - Current sun altitude
 * @returns {Object} Night color info
 */
function getNightPeriodColor(currentTime, sunTimes, fajrTime, maghribTime, altitude) {
  const current = currentTime.getHours() * 60 + currentTime.getMinutes();
  
  // Create a midnight reference
  const midnight = new Date(currentTime);
  midnight.setHours(0, 0, 0, 0);
  const midnightMinutes = 0;
  
  // Convert times to minutes for easier calculation
  const sunsetMinutes = sunTimes.sunset ? 
    sunTimes.sunset.getHours() * 60 + sunTimes.sunset.getMinutes() : 
    1080; // Default 6 PM
  
  // Calculate Fajr minutes (might be next day)
  let fajrMinutes;
  if (fajrTime) {
    if (typeof fajrTime === 'string') {
      const [hours, minutes] = fajrTime.split(':').map(Number);
      fajrMinutes = hours * 60 + minutes;
    } else {
      fajrMinutes = fajrTime.getHours() * 60 + fajrTime.getMinutes();
    }
  } else {
    fajrMinutes = 300; // Default 5 AM
  }
  
  // Define night period colors with smooth gradients
  let color, name, phase;
  
  // After sunset to midnight
  if (current > sunsetMinutes || current === 0) {
    const progressToMidnight = current > sunsetMinutes ? 
      (current - sunsetMinutes) / (1440 - sunsetMinutes) : 
      0;
    
    if (progressToMidnight < 0.2) {
      // Early evening (just after sunset)
      color = interpolateHexColors('#4c1d95', '#3730a3', progressToMidnight * 5);
      name = 'Early Night';
      phase = 'early-night';
    } else if (progressToMidnight < 0.5) {
      // Mid evening
      color = interpolateHexColors('#3730a3', '#1e3a8a', (progressToMidnight - 0.2) * 3.33);
      name = 'Evening';
      phase = 'evening';
    } else {
      // Late evening approaching midnight
      color = interpolateHexColors('#1e3a8a', '#1e1b4b', (progressToMidnight - 0.5) * 2);
      name = 'Late Evening';
      phase = 'late-evening';
    }
  }
  // Midnight to Fajr
  else if (current < fajrMinutes) {
    const progressToFajr = current / fajrMinutes;
    
    if (progressToFajr < 0.3) {
      // Early after midnight
      color = interpolateHexColors('#1e1b4b', '#0f172a', progressToFajr * 3.33);
      name = 'Deep Night';
      phase = 'deep-night';
    } else if (progressToFajr < 0.7) {
      // Middle of night
      color = interpolateHexColors('#0f172a', '#1e293b', (progressToFajr - 0.3) * 2.5);
      name = 'Late Night';
      phase = 'late-night';
    } else {
      // Approaching Fajr
      color = interpolateHexColors('#1e293b', '#4c1d95', (progressToFajr - 0.7) * 3.33);
      name = 'Pre-Dawn';
      phase = 'pre-dawn';
    }
  }
  // Between Fajr and sunrise (should not happen if altitude < -6)
  else {
    color = '#4c1d95';
    name = 'Dawn Twilight';
    phase = 'dawn';
  }
  
  return { color, name, phase };
}

/**
 * Determine the phase of day based on sun position
 * @param {number} altitude - Sun altitude in degrees
 * @param {number} azimuth - Sun azimuth in degrees
 * @returns {string} Phase name
 */
function determinePhase(altitude, azimuth) {
  if (altitude < -18) return 'astronomical-night';
  if (altitude < -12) return 'nautical-twilight';
  if (altitude < -6) return 'civil-twilight';
  if (altitude < 0) return 'blue-hour';
  if (altitude < 6) return 'golden-hour';
  if (altitude < 15) return azimuth < 0 ? 'early-morning' : 'late-afternoon';
  if (altitude < 30) return azimuth < 0 ? 'morning' : 'afternoon';
  if (altitude < 45) return azimuth < 0 ? 'late-morning' : 'early-afternoon';
  return 'solar-noon';
}

/**
 * Get a complete color scheme for the current time
 * @param {Date} date - Current date/time
 * @param {number} latitude - Location latitude
 * @param {number} longitude - Location longitude
 * @param {Object} options - Optional configuration
 * @returns {Object} Complete color scheme
 */
export function getSolarColorScheme(date, latitude, longitude, options = {}) {
  const primaryColorInfo = getDynamicSolarColor(date, latitude, longitude, options);
  const isDaytime = primaryColorInfo.altitude > 0;
  const isGoldenHour = primaryColorInfo.altitude >= 0 && primaryColorInfo.altitude <= 6;
  const isTwilight = primaryColorInfo.altitude >= -6 && primaryColorInfo.altitude < 0;
  
  return {
    primary: primaryColorInfo.color,
    primaryInfo: primaryColorInfo,
    
    // Secondary color - complementary based on altitude
    secondary: adjustColorForTime(primaryColorInfo.color, primaryColorInfo.altitude, 'secondary'),
    
    // Accent color - more vibrant version
    accent: adjustColorForTime(primaryColorInfo.color, primaryColorInfo.altitude, 'accent'),
    
    // Background colors based on sun position
    background: isDaytime ? '#ffffff' : 
                isTwilight ? '#1e293b' : '#0f172a',
    surface: isDaytime ? '#f8fafc' : 
             isTwilight ? '#334155' : '#1e293b',
    
    // Text colors
    text: isDaytime ? '#1e293b' : '#f1f5f9',
    textMuted: isDaytime ? '#64748b' : '#94a3b8',
    
    // Semantic colors adjusted for sun position
    success: isDaytime ? '#10b981' : '#34d399',
    warning: isGoldenHour ? '#f59e0b' : isDaytime ? '#f97316' : '#fb923c',
    error: isDaytime ? '#ef4444' : '#f87171',
    info: isDaytime ? '#3b82f6' : '#60a5fa',
    
    // Metadata
    sunAltitude: primaryColorInfo.altitude,
    sunAzimuth: primaryColorInfo.azimuth,
    isDaytime,
    isGoldenHour,
    isTwilight,
    phase: primaryColorInfo.phase
  };
}

/**
 * Adjust color based on time and purpose
 * @param {string} baseColor - Base color
 * @param {number} altitude - Sun altitude
 * @param {string} purpose - 'secondary' or 'accent'
 * @returns {string} Adjusted color
 */
function adjustColorForTime(baseColor, altitude, purpose) {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;
  
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  if (purpose === 'secondary') {
    // Shift hue slightly
    hsl.h = (hsl.h + 0.08) % 1;
    // Adjust saturation based on altitude
    if (altitude < 0) {
      hsl.s *= 0.7; // Less saturated at night
    } else if (altitude > 45) {
      hsl.s *= 0.9; // Slightly less saturated at noon
    }
  } else if (purpose === 'accent') {
    // Make more vibrant
    hsl.s = Math.min(1, hsl.s * 1.3);
    // Adjust lightness based on altitude
    if (altitude < 0) {
      hsl.l = Math.min(0.7, hsl.l * 1.2); // Lighter at night for visibility
    } else if (altitude > 45) {
      hsl.l = Math.max(0.4, hsl.l * 0.9); // Slightly darker at noon
    }
  }
  
  const adjustedRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
}

/**
 * Interpolate between two hex colors
 * @param {string} color1 - Start color (hex)
 * @param {string} color2 - End color (hex)
 * @param {number} factor - Interpolation factor (0-1)
 * @returns {string} Interpolated color (hex)
 */
export function interpolateHexColors(color1, color2, factor) {
  factor = Math.max(0, Math.min(1, factor));
  
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return color1;
  
  // Use a smoother interpolation curve
  const smoothFactor = smoothstep(factor);
  
  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * smoothFactor);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * smoothFactor);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * smoothFactor);
  
  return rgbToHex(r, g, b);
}

/**
 * Smoothstep function for smoother interpolation
 */
function smoothstep(x) {
  return x * x * (3 - 2 * x);
}

// Utility functions

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex color
 */
function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Convert RGB to HSL
 */
function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0; break;
    }
  }
  
  return { h, s, l };
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h, s, l) {
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Calculate luminance of a color (0 = dark, 1 = light)
 * @param {string} hexColor - Hex color string
 * @returns {number} Luminance value between 0 and 1
 */
export function getColorLuminance(hexColor) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return 0.5;
  
  // Use relative luminance formula (ITU-R BT.709)
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance;
}

/**
 * Get appropriate text colors based on background color
 * @param {string} backgroundColor - Background color in hex
 * @returns {Object} Text color scheme for readability
 */
export function getTextColorForBackground(backgroundColor) {
  const luminance = getColorLuminance(backgroundColor);
  
  if (luminance > 0.7) {
    // Very bright background (e.g., yellow at noon) - use very dark text
    return {
      primary: '#0f172a',      // Very dark slate
      secondary: '#1e293b',     // Dark slate  
      muted: '#334155',         // Dark gray
      shadow: '0 1px 2px rgba(255,255,255,0.5), 0 0 8px rgba(0,0,0,0.1)' // White glow + soft shadow
    };
  } else if (luminance > 0.5) {
    // Medium bright (e.g., amber) - use dark text with strong shadow
    return {
      primary: '#1e293b',      // Dark slate
      secondary: '#334155',     // Dark gray
      muted: '#475569',         // Medium gray
      shadow: '0 1px 3px rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.2)' // Double shadow for contrast
    };
  } else if (luminance > 0.3) {
    // Medium (e.g., orange) - use light text with dark shadow
    return {
      primary: '#ffffff',      // White
      secondary: '#f8fafc',    // Near white
      muted: '#f1f5f9',       // Light gray
      shadow: '0 2px 4px rgba(0,0,0,0.4)' // Strong dark shadow
    };
  } else {
    // Dark background (e.g., night, twilight) - use light text
    return {
      primary: '#ffffff',      // White
      secondary: '#f1f5f9',    // Light gray
      muted: '#e2e8f0',       // Pale gray
      shadow: '0 1px 3px rgba(0,0,0,0.5)' // Subtle shadow for depth
    };
  }
}

// Export all functions
const solarColorSystem = {
  getDynamicSolarColor,
  getSolarColorScheme,
  interpolateHexColors,
  getColorLuminance,
  getTextColorForBackground,
  ALTITUDE_COLOR_MAP
};

export default solarColorSystem;